import { useState, useEffect } from 'react';
import { api } from './api';
import TaskList from './components/TaskList';
import SpawnForm from './components/SpawnForm';
import Stats from './components/Stats';
import { Activity, Plus, BarChart3, RefreshCw, Server } from 'lucide-react';
import './App.css';

function App() {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks');
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [tasksRes, statsRes, healthRes] = await Promise.all([
        api.getTasks(),
        api.getStats(),
        api.getHealth(),
      ]);

      setTasks(tasksRes.tasks || []);
      setStats(statsRes);
      setHealth(healthRes);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const handleSpawn = async (type, description) => {
    try {
      await api.spawnTask(type, description);
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCheck = async () => {
    try {
      await api.runCheck();
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <Server className="logo-icon" />
            <h1>JDiaz-MC</h1>
          </div>
          <div className="header-meta">
            {health && (
              <span className={`status ${health.status === 'ok' ? 'online' : 'offline'}`}>
                ● {health.status === 'ok' ? 'Online' : 'Offline'}
              </span>
            )}
            <button onClick={fetchData} className="btn-refresh" disabled={loading}>
              <RefreshCw className={loading ? 'spin' : ''} size={18} />
            </button>
          </div>
        </div>
      </header>

      <nav className="nav">
        <button 
          className={activeTab === 'tasks' ? 'active' : ''} 
          onClick={() => setActiveTab('tasks')}
        >
          <Activity size={18} />
          Tasks
        </button>
        <button 
          className={activeTab === 'spawn' ? 'active' : ''} 
          onClick={() => setActiveTab('spawn')}
        >
          <Plus size={18} />
          Spawn
        </button>
        <button 
          className={activeTab === 'stats' ? 'active' : ''} 
          onClick={() => setActiveTab('stats')}
        >
          <BarChart3 size={18} />
          Stats
        </button>
      </nav>

      <main className="main">
        {error && (
          <div className="error-banner">
            Error: {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {loading && tasks.length === 0 ? (
          <div className="loading">
            <RefreshCw className="spin" size={32} />
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === 'tasks' && (
              <TaskList 
                tasks={tasks} 
                onRefresh={fetchData}
                onCheck={handleCheck}
              />
            )}
            {activeTab === 'spawn' && (
              <SpawnForm onSpawn={handleSpawn} />
            )}
            {activeTab === 'stats' && (
              <Stats stats={stats} tasks={tasks} />
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default App;
